/**
 * Centralized country configuration for job search.
 * Each entry: { label, code, flag, language }
 *   - label: display name
 *   - code: locale format "lang_COUNTRY" — backend splits on "_" to get ISO 3166-1 alpha-2
 *   - flag: emoji flag
 *   - language: human-readable primary language (shown as hint in UI)
 */
export const COUNTRIES = [
  // Anglophone
  { label: 'USA',          code: 'en_US', flag: '🇺🇸', language: 'English' },
  { label: 'UK',           code: 'en_GB', flag: '🇬🇧', language: 'English' },
  { label: 'Canada',       code: 'en_CA', flag: '🇨🇦', language: 'English' },
  { label: 'Australia',    code: 'en_AU', flag: '🇦🇺', language: 'English' },
  { label: 'New Zealand',  code: 'en_NZ', flag: '🇳🇿', language: 'English' },
  { label: 'Ireland',      code: 'en_IE', flag: '🇮🇪', language: 'English' },

  // Western Europe
  { label: 'Germany',      code: 'de_DE', flag: '🇩🇪', language: 'German' },
  { label: 'France',       code: 'fr_FR', flag: '🇫🇷', language: 'French' },
  { label: 'Netherlands',  code: 'nl_NL', flag: '🇳🇱', language: 'Dutch' },
  { label: 'Spain',        code: 'es_ES', flag: '🇪🇸', language: 'Spanish' },
  { label: 'Italy',        code: 'it_IT', flag: '🇮🇹', language: 'Italian' },
  { label: 'Switzerland',  code: 'de_CH', flag: '🇨🇭', language: 'German' },
  { label: 'Austria',      code: 'de_AT', flag: '🇦🇹', language: 'German' },
  { label: 'Belgium',      code: 'nl_BE', flag: '🇧🇪', language: 'Dutch' },
  { label: 'Portugal',     code: 'pt_PT', flag: '🇵🇹', language: 'Portuguese' },

  // Nordics
  { label: 'Sweden',       code: 'sv_SE', flag: '🇸🇪', language: 'Swedish' },
  { label: 'Norway',       code: 'no_NO', flag: '🇳🇴', language: 'Norwegian' },
  { label: 'Denmark',      code: 'da_DK', flag: '🇩🇰', language: 'Danish' },
  { label: 'Finland',      code: 'fi_FI', flag: '🇫🇮', language: 'Finnish' },

  // Central / Eastern Europe
  { label: 'Poland',       code: 'pl_PL', flag: '🇵🇱', language: 'Polish' },
  { label: 'Czechia',      code: 'cs_CZ', flag: '🇨🇿', language: 'Czech' },
  { label: 'Slovakia',     code: 'sk_SK', flag: '🇸🇰', language: 'Slovak' },
  { label: 'Hungary',      code: 'hu_HU', flag: '🇭🇺', language: 'Hungarian' },
  { label: 'Romania',      code: 'ro_RO', flag: '🇷🇴', language: 'Romanian' },
  { label: 'Ukraine',      code: 'uk_UA', flag: '🇺🇦', language: 'Ukrainian' },
  { label: 'Slovenia',     code: 'sl_SI', flag: '🇸🇮', language: 'Slovenian' },
  { label: 'Croatia',      code: 'hr_HR', flag: '🇭🇷', language: 'Croatian' },
  { label: 'Bulgaria',     code: 'bg_BG', flag: '🇧🇬', language: 'Bulgarian' },

  // Asia / Other
  { label: 'Japan',        code: 'ja_JP', flag: '🇯🇵', language: 'Japanese' },
  { label: 'Singapore',    code: 'en_SG', flag: '🇸🇬', language: 'English' },
  { label: 'UAE',          code: 'ar_AE', flag: '🇦🇪', language: 'Arabic' },
  { label: 'Israel',       code: 'he_IL', flag: '🇮🇱', language: 'Hebrew' },
];

/** Default country label used when nothing is selected */
export const DEFAULT_COUNTRY = 'USA';

/**
 * Get the locale code for a country label.
 * Falls back to the default country code if not found.
 */
export function getCountryCode(label) {
  const entry = COUNTRIES.find((c) => c.label === label);
  return entry ? entry.code : getCountryCode(DEFAULT_COUNTRY);
}

/**
 * Get the human-readable language hint for a country label.
 * Returns a formatted string like "English language only".
 */
export function getLanguageHint(label) {
  const entry = COUNTRIES.find((c) => c.label === label);
  return entry ? `${entry.language} language only` : 'English language only';
}
